import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const LoanCalculatorPage = () => {
  // Amortized Loan State
  const [amortLoanAmount, setAmortLoanAmount] = useState('100000');
  const [amortLoanTerm, setAmortLoanTerm] = useState('10');
  const [amortTermType, setAmortTermType] = useState('years');
  const [amortInterestRate, setAmortInterestRate] = useState('6');
  const [amortCompound, setAmortCompound] = useState('monthly');
  const [amortPayBack, setAmortPayBack] = useState('monthly');
  const [amortResult, setAmortResult] = useState(null);
  const [showAmortTable, setShowAmortTable] = useState(false);

  // Deferred Payment Loan State
  const [deferLoanAmount, setDeferLoanAmount] = useState('100000');
  const [deferLoanTerm, setDeferLoanTerm] = useState('10');
  const [deferTermType, setDeferTermType] = useState('years');
  const [deferInterestRate, setDeferInterestRate] = useState('6');
  const [deferCompound, setDeferCompound] = useState('monthly');
  const [deferResult, setDeferResult] = useState(null);

  // Bond Calculator State
  const [bondDueAmount, setBondDueAmount] = useState('100000');
  const [bondLoanTerm, setBondLoanTerm] = useState('10');
  const [bondTermType, setBondTermType] = useState('years');
  const [bondInterestRate, setBondInterestRate] = useState('6');
  const [bondCompound, setBondCompound] = useState('monthly');
  const [bondResult, setBondResult] = useState(null);

  const compoundOptions = [
    { value: 'annually', label: 'Annually (APY)', n: 1 },
    { value: 'semiannually', label: 'Semi-annually', n: 2 },
    { value: 'quarterly', label: 'Quarterly', n: 4 },
    { value: 'monthly', label: 'Monthly (APR)', n: 12 },
    { value: 'semimonthly', label: 'Semi-monthly', n: 24 },
    { value: 'biweekly', label: 'Biweekly', n: 26 },
    { value: 'weekly', label: 'Weekly', n: 52 },
    { value: 'daily', label: 'Daily', n: 365 },
  ];

  const payBackOptions = [
    { value: 'daily', label: 'Every Day', n: 365 },
    { value: 'weekly', label: 'Every Week', n: 52 },
    { value: 'biweekly', label: 'Every 2 Weeks', n: 26 },
    { value: 'semimonthly', label: 'Every Half Month', n: 24 },
    { value: 'monthly', label: 'Every Month', n: 12 },
    { value: 'quarterly', label: 'Every Quarter', n: 4 },
    { value: 'semiannually', label: 'Every 6 Months', n: 2 },
    { value: 'annually', label: 'Every Year', n: 1 },
  ];

  const getCompoundN = (compound) => compoundOptions.find(c => c.value === compound)?.n || 12;
  const getPayBackN = (payBack) => payBackOptions.find(p => p.value === payBack)?.n || 12;
  const getPayBackLabel = (payBack) => {
    const labels = {
      daily: 'Day', weekly: 'Week', biweekly: '2 Weeks', semimonthly: 'Half Month',
      monthly: 'Month', quarterly: 'Quarter', semiannually: '6 Months', annually: 'Year'
    };
    return labels[payBack] || 'Month';
  };

  // Amortized Loan Calculation
  const calculateAmortizedLoan = () => {
    const principal = parseFloat(amortLoanAmount) || 0;
    const annualRate = parseFloat(amortInterestRate) / 100;
    const termMonths = amortTermType === 'years' 
      ? parseFloat(amortLoanTerm) * 12 
      : parseFloat(amortLoanTerm);
    
    const compoundN = getCompoundN(amortCompound);
    const payBackN = getPayBackN(amortPayBack);
    
    // Calculate effective rate per payment period
    const effectiveAnnualRate = Math.pow(1 + annualRate / compoundN, compoundN) - 1;
    const ratePerPeriod = Math.pow(1 + effectiveAnnualRate, 1 / payBackN) - 1;
    const totalPayments = Math.round(termMonths * payBackN / 12);

    if (principal > 0 && totalPayments > 0) {
      let payment;
      if (ratePerPeriod === 0) {
        payment = principal / totalPayments;
      } else {
        payment = (principal * ratePerPeriod * Math.pow(1 + ratePerPeriod, totalPayments)) / 
                  (Math.pow(1 + ratePerPeriod, totalPayments) - 1);
      }

      const totalPaid = payment * totalPayments;
      const totalInterest = totalPaid - principal;

      // Generate amortization schedule
      const schedule = [];
      let balance = principal;
      for (let i = 1; i <= totalPayments; i++) {
        const interestPayment = balance * ratePerPeriod;
        const principalPayment = payment - interestPayment;
        balance = Math.max(0, balance - principalPayment);
        schedule.push({
          period: i,
          payment: payment,
          principal: principalPayment,
          interest: interestPayment,
          balance: balance
        });
      }

      setAmortResult({
        payment,
        totalPayments,
        totalPaid,
        totalInterest,
        principal,
        principalPercent: ((principal / totalPaid) * 100).toFixed(0),
        interestPercent: ((totalInterest / totalPaid) * 100).toFixed(0),
        schedule
      });
    }
  };

  // Deferred Payment Loan Calculation
  const calculateDeferredLoan = () => {
    const principal = parseFloat(deferLoanAmount) || 0;
    const annualRate = parseFloat(deferInterestRate) / 100;
    const years = deferTermType === 'years' 
      ? parseFloat(deferLoanTerm) 
      : parseFloat(deferLoanTerm) / 12;
    
    const compoundN = getCompoundN(deferCompound);

    if (principal > 0 && years > 0) {
      const amountDue = principal * Math.pow(1 + annualRate / compoundN, compoundN * years);
      const totalInterest = amountDue - principal;

      setDeferResult({
        amountDue,
        totalInterest,
        principal,
        principalPercent: ((principal / amountDue) * 100).toFixed(0),
        interestPercent: ((totalInterest / amountDue) * 100).toFixed(0)
      });
    }
  };

  // Bond Calculation
  const calculateBond = () => {
    const faceValue = parseFloat(bondDueAmount) || 0;
    const annualRate = parseFloat(bondInterestRate) / 100;
    const years = bondTermType === 'years' 
      ? parseFloat(bondLoanTerm) 
      : parseFloat(bondLoanTerm) / 12;
    
    const compoundN = getCompoundN(bondCompound);

    if (faceValue > 0 && years > 0) {
      const presentValue = faceValue / Math.pow(1 + annualRate / compoundN, compoundN * years);
      const totalInterest = faceValue - presentValue;

      setBondResult({
        presentValue,
        totalInterest,
        faceValue,
        principalPercent: ((presentValue / faceValue) * 100).toFixed(0),
        interestPercent: ((totalInterest / faceValue) * 100).toFixed(0)
      });
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(num);
  };

  // Pie Chart Component
  const PieChart = ({ percent1, percent2, color1 = '#06b6d4', color2 = '#f59e0b' }) => (
    <div className="relative w-24 h-24">
      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
        <circle cx="50" cy="50" r="40" fill="transparent" stroke={color1} strokeWidth="20"
          strokeDasharray={`${percent1 * 2.51} 251`} />
        <circle cx="50" cy="50" r="40" fill="transparent" stroke={color2} strokeWidth="20"
          strokeDasharray={`${percent2 * 2.51} 251`} strokeDashoffset={`-${percent1 * 2.51}`} />
      </svg>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/financial-calculators" className="text-cyan-400 hover:text-cyan-300">Financial Calculators</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Loan Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Loan Calculator</h1>
          <p className="text-gray-400 mb-6">
            A loan is a contract between a borrower and a lender in which the borrower receives an amount of money (principal) 
            that they are obligated to pay back in the future. Most loans can be categorized into one of three categories:
          </p>

          <div className="mb-6 space-y-2">
            <p className="text-gray-300">1. <span className="text-cyan-400">Amortized Loan:</span> Fixed payments paid periodically until loan maturity</p>
            <p className="text-gray-300">2. <span className="text-cyan-400">Deferred Payment Loan:</span> Single lump sum paid at loan maturity</p>
            <p className="text-gray-300">3. <span className="text-cyan-400">Bond:</span> Predetermined lump sum paid at loan maturity</p>
          </div>

          {/* Amortized Loan Calculator */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-2">Amortized Loan: Paying Back a Fixed Amount Periodically</h2>
            <p className="text-gray-400 text-sm mb-4">
              Use this calculator for basic calculations of common loan types such as mortgages, auto loans, student loans, or personal loans.
            </p>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Loan Amount</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={amortLoanAmount} onChange={(e) => setAmortLoanAmount(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Loan Term</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={amortLoanTerm} onChange={(e) => setAmortLoanTerm(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={amortTermType} onChange={(e) => setAmortTermType(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="years">years</option>
                    <option value="months">months</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Interest Rate</label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.01" value={amortInterestRate} onChange={(e) => setAmortInterestRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Compound</label>
                <select value={amortCompound} onChange={(e) => setAmortCompound(e.target.value)}
                  className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                  {compoundOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Pay Back</label>
                <select value={amortPayBack} onChange={(e) => setAmortPayBack(e.target.value)}
                  className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                  {payBackOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <Button onClick={calculateAmortizedLoan} className="bg-cyan-600 hover:bg-cyan-500 text-white">
                Calculate
              </Button>
            </div>

            {amortResult && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Results</h3>
                </div>

                <div className="flex flex-wrap items-start gap-6">
                  <div className="flex-1 min-w-[200px] space-y-3">
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Payment Every {getPayBackLabel(amortPayBack)}</span>
                      <span className="font-bold text-white">{formatCurrency(amortResult.payment)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total of {amortResult.totalPayments} Payments</span>
                      <span className="font-bold text-white">{formatCurrency(amortResult.totalPaid)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Interest</span>
                      <span className="font-bold text-white">{formatCurrency(amortResult.totalInterest)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-2">
                    <PieChart percent1={amortResult.principalPercent} percent2={amortResult.interestPercent} />
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{amortResult.principalPercent}% Principal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{amortResult.interestPercent}% Interest</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button onClick={() => setShowAmortTable(!showAmortTable)}
                  className="mt-4 text-cyan-400 hover:text-cyan-300 text-sm">
                  {showAmortTable ? 'Hide' : 'View'} Amortization Table
                </button>

                {showAmortTable && (
                  <div className="mt-4 overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#0d0d0d]">
                        <tr className="border-b border-[#2a2a2a]">
                          <th className="text-left py-2 px-2 text-gray-400">#</th>
                          <th className="text-right py-2 px-2 text-gray-400">Payment</th>
                          <th className="text-right py-2 px-2 text-gray-400">Principal</th>
                          <th className="text-right py-2 px-2 text-gray-400">Interest</th>
                          <th className="text-right py-2 px-2 text-gray-400">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortResult.schedule.slice(0, 60).map((row) => (
                          <tr key={row.period} className="border-b border-[#2a2a2a]/50 hover:bg-[#1a1a1a]">
                            <td className="py-1.5 px-2 text-white">{row.period}</td>
                            <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.payment)}</td>
                            <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.principal)}</td>
                            <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.interest)}</td>
                            <td className="py-1.5 px-2 text-right text-white">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {amortResult.schedule.length > 60 && (
                      <p className="text-center text-gray-500 text-sm py-2">
                        Showing first 60 of {amortResult.totalPayments} payments
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Deferred Payment Loan Calculator */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-2">Deferred Payment Loan: Paying Back a Lump Sum Due at Maturity</h2>
            <p className="text-gray-400 text-sm mb-4">
              Many commercial loans or short-term loans fall into this category with a single, large lump sum due at maturity.
            </p>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Loan Amount</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={deferLoanAmount} onChange={(e) => setDeferLoanAmount(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Loan Term</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={deferLoanTerm} onChange={(e) => setDeferLoanTerm(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={deferTermType} onChange={(e) => setDeferTermType(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="years">years</option>
                    <option value="months">months</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Interest Rate</label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.01" value={deferInterestRate} onChange={(e) => setDeferInterestRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-28">Compound</label>
                <select value={deferCompound} onChange={(e) => setDeferCompound(e.target.value)}
                  className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                  {compoundOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <Button onClick={calculateDeferredLoan} className="bg-cyan-600 hover:bg-cyan-500 text-white">
                Calculate
              </Button>
            </div>

            {deferResult && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Results</h3>
                </div>

                <div className="flex flex-wrap items-start gap-6">
                  <div className="flex-1 min-w-[200px] space-y-3">
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Amount Due at Loan Maturity</span>
                      <span className="font-bold text-white">{formatCurrency(deferResult.amountDue)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Interest</span>
                      <span className="font-bold text-white">{formatCurrency(deferResult.totalInterest)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-2">
                    <PieChart percent1={deferResult.principalPercent} percent2={deferResult.interestPercent} />
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{deferResult.principalPercent}% Principal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{deferResult.interestPercent}% Interest</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bond Calculator */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-2">Bond: Paying Back a Predetermined Amount Due at Loan Maturity</h2>
            <p className="text-gray-400 text-sm mb-4">
              Use this calculator to compute the initial value of a bond/loan based on a predetermined face value to be paid back at bond/loan maturity.
            </p>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Predetermined Due Amount</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={bondDueAmount} onChange={(e) => setBondDueAmount(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Loan Term</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={bondLoanTerm} onChange={(e) => setBondLoanTerm(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={bondTermType} onChange={(e) => setBondTermType(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="years">years</option>
                    <option value="months">months</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Interest Rate</label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.01" value={bondInterestRate} onChange={(e) => setBondInterestRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-36">Compound</label>
                <select value={bondCompound} onChange={(e) => setBondCompound(e.target.value)}
                  className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                  {compoundOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <Button onClick={calculateBond} className="bg-cyan-600 hover:bg-cyan-500 text-white">
                Calculate
              </Button>
            </div>

            {bondResult && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Results</h3>
                </div>

                <div className="flex flex-wrap items-start gap-6">
                  <div className="flex-1 min-w-[200px] space-y-3">
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Amount Received When Loan Starts</span>
                      <span className="font-bold text-white">{formatCurrency(bondResult.presentValue)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Interest</span>
                      <span className="font-bold text-white">{formatCurrency(bondResult.totalInterest)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-2">
                    <PieChart percent1={bondResult.principalPercent} percent2={bondResult.interestPercent} />
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{bondResult.principalPercent}% Principal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{bondResult.interestPercent}% Interest</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Related Calculators */}
          <div className="flex flex-wrap gap-2 text-sm mb-8">
            <span className="text-gray-500">Related:</span>
            <Link to="/mortgage-calculator" className="text-cyan-400 hover:text-cyan-300">Mortgage Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/auto-loan-calculator" className="text-cyan-400 hover:text-cyan-300">Auto Loan Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/interest-calculator" className="text-cyan-400 hover:text-cyan-300">Interest Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/compound-interest-calculator" className="text-cyan-400 hover:text-cyan-300">Compound Interest Calculator</Link>
          </div>

          {/* Information Sections */}
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Amortized Loan: Fixed Amount Paid Periodically</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Many consumer loans fall into this category of loans that have regular payments that are amortized uniformly 
                over their lifetime. Routine payments are made on principal and interest until the loan reaches maturity 
                (is entirely paid off). Some of the most familiar amortized loans include mortgages, car loans, student loans, 
                and personal loans.
              </p>

              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { name: 'Mortgage Calculator', path: '/mortgage-calculator' },
                  { name: 'Auto Loan Calculator', path: '/auto-loan-calculator' },
                  { name: 'Investment Calculator', path: '/investment-calculator' },
                  { name: 'Compound Interest Calculator', path: '/compound-interest-calculator' },
                ].map((calc) => (
                  <Link key={calc.path} to={calc.path}
                    className="p-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 transition-colors">
                    {calc.name}
                  </Link>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Loan Basics for Borrowers</h2>
              
              <div className="space-y-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Interest Rate</h3>
                  <p className="text-gray-400 text-sm">
                    Nearly all loan structures include interest, which is the profit that banks or lenders make on loans. 
                    Interest rate is the percentage of a loan paid by borrowers to lenders. Loan interest is usually expressed 
                    in APR, or annual percentage rate, which includes both interest and fees.
                  </p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Compounding Frequency</h3>
                  <p className="text-gray-400 text-sm">
                    Compound interest is interest that is earned not only on the initial principal but also on accumulated 
                    interest from previous periods. Generally, the more frequently compounding occurs, the higher the total 
                    amount due on the loan.
                  </p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Loan Term</h3>
                  <p className="text-gray-400 text-sm">
                    A loan term is the duration of the loan, given that required minimum payments are made each month. 
                    Generally, the longer the term, the more interest will be accrued over time, raising the total cost 
                    of the loan for borrowers, but reducing the periodic payments.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Consumer Loans</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Secured Loans</h3>
                  <p className="text-gray-400 text-sm">
                    A secured loan means that the borrower has put up some asset as a form of collateral before being 
                    granted a loan. The most common secured loans are mortgages and auto loans. Secured loans generally 
                    have a higher chance of approval compared to unsecured loans.
                  </p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Unsecured Loans</h3>
                  <p className="text-gray-400 text-sm">
                    An unsecured loan is an agreement to pay a loan back without collateral. Unsecured loans generally 
                    feature higher interest rates, lower borrowing limits, and shorter repayment terms than secured loans. 
                    Examples include credit cards, personal loans, and student loans.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* More Financial Calculators */}
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Mortgage', path: '/mortgage-calculator' },
                { name: 'Loan', path: '/loan-calculator' },
                { name: 'Auto Loan', path: '/auto-loan-calculator' },
                { name: 'Interest', path: '/interest-calculator' },
                { name: 'Payment', path: '/payment-calculator' },
                { name: 'Retirement', path: '/retirement-calculator' },
                { name: 'Amortization', path: '/amortization-calculator' },
                { name: 'Investment', path: '/investment-calculator' },
                { name: 'Compound Interest', path: '/compound-interest-calculator' },
                { name: 'Sales Tax', path: '/sales-tax-calculator' },
              ].map((calc) => (
                <Link key={calc.path} to={calc.path}
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors">
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LoanCalculatorPage;
